import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./MyPosts.css";
import { getScheduledPosts, deleteScheduledPost } from "../../services/scheduledPostsApi";

const API_URL = process.env.REACT_APP_API_URL;

const MyPosts = () => {
  const [activeTab, setActiveTab] = useState("published"); // "published" or "scheduled"
  const [publishedPosts, setPublishedPosts] = useState([]);
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null); // for modal
  const [canceling, setCanceling] = useState(null); // track which post is being canceled

  const fetchPublishedPosts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/posts`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to fetch posts");
      const data = await response.json();
      setPublishedPosts(data.posts || []);
    } catch (err) {
      console.error("Error fetching published posts:", err);
    }
  };

  const fetchScheduledPosts = async () => {
    try {
      const data = await getScheduledPosts({ status: "scheduled", limit: 50 });
      setScheduledPosts(data || []);
    } catch (err) {
      console.error("Error fetching scheduled posts:", err);
    }
  };

  useEffect(() => {
    const fetchAllPosts = async () => {
      setLoading(true);
      await Promise.all([fetchPublishedPosts(), fetchScheduledPosts()]);
      setLoading(false);
    };
    fetchAllPosts();

    // Refresh every 30 seconds to catch newly published posts
    const interval = setInterval(fetchAllPosts, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleCancelScheduled = async (postId) => {
    if (!window.confirm("Are you sure you want to cancel this scheduled post?")) {
      return;
    }

    setCanceling(postId);
    try {
      await deleteScheduledPost(postId);
      // Refresh scheduled posts
      await fetchScheduledPosts();
    } catch (err) {
      console.error("Error canceling post:", err);
      alert("Failed to cancel post. Please try again.");
    } finally {
      setCanceling(null);
    }
  };

  const formatDate = (dateStr) => {
    // Backend sends UTC datetime strings without 'Z', so we need to append it
    const utcDateStr = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z';
    const date = new Date(utcDateStr);
    return date.toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatScheduledDate = (dateStr) => {
    // Backend sends UTC datetime strings without 'Z', so we need to append it
    const utcDateStr = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z';
    const date = new Date(utcDateStr);
    return date.toLocaleString(undefined, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const posts = activeTab === "published" ? publishedPosts : scheduledPosts;

  return (
    <div className="myposts-wrapper">
      <div className="myposts-header">
        <h2 className="myposts-title">My Posts</h2>
        <div className="myposts-tabs">
          <button
            className={`myposts-tab ${activeTab === "published" ? "active" : ""}`}
            onClick={() => setActiveTab("published")}
          >
            Published ({publishedPosts.length})
          </button>
          <button
            className={`myposts-tab ${activeTab === "scheduled" ? "active" : ""}`}
            onClick={() => setActiveTab("scheduled")}
          >
            Scheduled ({scheduledPosts.length})
          </button>
        </div>
      </div>

      {loading ? (
        <p className="myposts-loading">Loading posts...</p>
      ) : posts.length === 0 ? (
        <p className="myposts-empty">
          {activeTab === "published"
            ? "No published posts yet. Generate one to see it here!"
            : "No scheduled posts. Schedule a post to see it here!"}
        </p>
      ) : (
        <div className="myposts-grid">
          {posts.map((post) => (
            <div key={post._id || post.id} className="myposts-card">
              <p className="myposts-content">
                {post.content.length > 120
                  ? post.content.slice(0, 120) + "..."
                  : post.content}
              </p>
              <div className="myposts-footer">
                <div className="myposts-date">
                  {activeTab === "published" ? (
                    <>
                      <span className="linkedin-icon">in</span>
                      {formatDate(post.created_at)}
                    </>
                  ) : (
                    <>
                      <span className="scheduled-icon">📅</span>
                      {formatScheduledDate(post.scheduled_at)}
                    </>
                  )}
                </div>
                <div className="myposts-actions">
                  {activeTab === "published" && post.post_urn && (
                    <button
                      className="myposts-view-btn"
                      onClick={() => setSelectedPost(post)}
                    >
                      View
                    </button>
                  )}
                  {activeTab === "scheduled" && (
                    <>
                      <button
                        className="myposts-view-btn"
                        onClick={() => setSelectedPost(post)}
                      >
                        View
                      </button>
                      <button
                        className="myposts-cancel-btn"
                        onClick={() => handleCancelScheduled(post.id)}
                        disabled={canceling === post.id}
                      >
                        {canceling === post.id ? "Canceling..." : "Cancel"}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal with Framer Motion */}
      <AnimatePresence>
        {selectedPost && (
          <motion.div
            className="myposts-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedPost(null)}
          >
            <motion.div
              className="myposts-modal"
              initial={{ opacity: 0, scale: 0.9, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 30 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                className="myposts-modal-close"
                onClick={() => setSelectedPost(null)}
              >
                ✕
              </button>

              {/* Modal content */}
              <div className="myposts-modal-content">
                <h3 className="myposts-modal-title">
                  {activeTab === "published" ? "Published Post" : "Scheduled Post"}
                </h3>
                <p className="myposts-modal-text">{selectedPost.content}</p>
                {activeTab === "scheduled" && (
                  <div className="myposts-modal-schedule-info">
                    <strong>Scheduled for:</strong> {formatScheduledDate(selectedPost.scheduled_at)}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="myposts-modal-footer">
                {activeTab === "published" && selectedPost.post_urn && (
                  <a
                    href={`https://www.linkedin.com/feed/update/${selectedPost.post_urn}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="myposts-modal-btn"
                  >
                    View on LinkedIn
                  </a>
                )}
                {activeTab === "scheduled" && (
                  <button
                    className="myposts-modal-cancel-btn"
                    onClick={() => {
                      handleCancelScheduled(selectedPost.id);
                      setSelectedPost(null);
                    }}
                  >
                    Cancel Scheduled Post
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MyPosts;
